import {
  FormControl,
  FormLabel,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
} from '@mui/material';
import { GenericParser, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useListViewContext } from '../context/ListViewContext';

/**
 * Option for the button group filter (e.g. true / false).
 */
export interface ButtonGroupOption {
  /** The value of the option (e.g. true or false for boolean filters). */
  value: boolean;
  /** Message descriptor for the button label (i18n). */
  message: MessageDescriptor;
  /** Optional MUI sx prop to customize this button's styles. */
  sx?: SxProps<Theme>;
  /** Optional MUI color prop to customize this button's color. */
  color?: ToggleButtonProps['color'];
}

/**
 * Props for the generic button group filter component.
 * Renders a toggle button group for boolean-like filters instead of a single checkbox.
 */
export interface GenericButtonGroupFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label */
  label: MessageDescriptor | false;
  /** Options for the buttons (e.g. [{ value: true, message: Sí }, { value: false, message: No }]). */
  options: readonly ButtonGroupOption[];
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). Handles defaults via .withDefault(). */
  parser: GenericParser<boolean>;
  /** Whether the filter is active (addable filters). Undefined for permanent filters. */
  isActive?: boolean;
  /** Callback when filter is cleared (addable filters). */
  onRemove?: () => void;
}

/**
 * Generic boolean filter rendered as a button group (e.g. Sí / No).
 * Writes go through ListView context (setFilterValue) so filter changes are bundled with a page reset.
 */
export function GenericButtonGroupFilter({
  name,
  label,
  options,
  parser,
  isActive,
  onRemove: _onRemove,
}: GenericButtonGroupFilterProps) {
  const { $t } = useIntl();
  const { setFilterValue } = useListViewContext();
  const [value] = useQueryState(name, parser);

  const handleChange = (_e: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue !== null) {
      setFilterValue(name, newValue);
    }
  };

  // If it's an addable filter with no value (false) and not active, don't render
  if (isActive === false && !value) {
    return null;
  }

  const translatedLabel = label === false ? '' : $t(label);

  return (
    <FormControl size="small" component="fieldset">
      {translatedLabel ? (
        <FormLabel sx={{ typography: 'caption', color: 'text.secondary', mb: 0, mt: '-10px' }}>
          {translatedLabel}
        </FormLabel>
      ) : null}
      <ToggleButtonGroup value={value} exclusive onChange={handleChange} size="small">
        {options.map((opt) => (
          <ToggleButton
            key={String(opt.value)}
            value={opt.value}
            size="small"
            sx={
              [
                { py: '0px', mt: '-1px', minWidth: '5ch' },
                ...(opt.sx ? (Array.isArray(opt.sx) ? opt.sx : [opt.sx]) : []),
              ] as SxProps<Theme>
            }
            color={opt.color}
          >
            {$t(opt.message)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormControl>
  );
}
